import { CircleHelp } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import FreshpaintLogoLight from "@/components/FpLogoLight";
import { LoadingSpinner } from "@/design-system/components/LoadingSpinner/LoadingSpinner";
import { Typography } from "@/design-system/components/Typography/Typography";
import PageWithTitle from "@/PageWithTitle";
import { WtmPublicTrackerPreview } from "@/trackers/WtmPublicTrackerPreview";

import { fetchPublicTrackerDetails, type PublicTrackerDetails } from "./api";

const loginPath = (trackerKey: string) => {
  const next = `/monitoring/trackingTools/${encodeURIComponent(trackerKey)}`;
  return `/login?${new URLSearchParams({ next }).toString()}`;
};

function shareContextCopy(sharedBy?: string) {
  const sender = sharedBy?.trim();
  if (sender) {
    return `${sender} shared a tracker from Freshpaint Web Tracker Monitoring`;
  }
  return "A tracker was shared from Freshpaint Web Tracker Monitoring.";
}

function TrackerDetails({
  details,
  signInHref,
}: {
  details: PublicTrackerDetails;
  signInHref: string;
}) {
  const sender = details.sharedBy?.trim();
  const note = details.note?.trim();

  return (
    <div className="flex w-full max-w-[800px] flex-col gap-6">
      {sender && note ? (
        <section className="border-borderSubtle bg-surface-default flex flex-col gap-1 rounded-lg border px-4 py-3">
          <p className="text-label-xs text-textSubtle">Note from {sender}</p>
          <Typography variant="body-sm">{note}</Typography>
        </section>
      ) : null}

      <article className="border-borderSubtle bg-surface-default rounded-lg border px-3.5 py-3">
        <WtmPublicTrackerPreview
          trackerName={details.trackerName}
          riskLevel={details.riskLevel}
          description={details.description}
          guidance={details.guidance}
        />
      </article>

      <p className="text-body-sm text-textSubtle flex items-center justify-center gap-1 py-6">
        Already have a Freshpaint account?
        <a
          href={signInHref}
          className="text-link text-label-xs cursor-pointer hover:underline"
        >
          Sign in
        </a>
      </p>
    </div>
  );
}

function PublicTrackerDetailPage() {
  const { trackerKey = "" } = useParams<{ trackerKey: string }>();
  const { search } = useLocation();
  const shareId = new URLSearchParams(search).get("share") || undefined;
  const [details, setDetails] = useState<PublicTrackerDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setIsError(false);

    fetchPublicTrackerDetails(trackerKey, shareId)
      .then((result) => {
        if (active) {
          setDetails(result);
        }
      })
      .catch(() => {
        if (active) {
          setIsError(true);
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [trackerKey, shareId]);

  const title = details?.trackerName ?? "Tracker details";
  const signInHref = loginPath(trackerKey);

  return (
    <PageWithTitle title={title}>
      <div className="bg-surface-hover min-h-screen">
        <header className="bg-nav-shell px-12 py-5">
          <div className="flex max-w-[1440px] flex-col items-start gap-2">
            <FreshpaintLogoLight
              className="h-auto w-28"
              aria-label="Freshpaint"
            />
            <p className="text-body-sm text-textDefaultInverse">
              {shareContextCopy(details?.sharedBy)}
            </p>
          </div>
        </header>
        <main className="flex justify-center px-12 pt-10 pb-12">
          {isLoading && (
            <div className="flex min-h-80 items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
          {!isLoading && isError && (
            <section className="border-borderSubtle bg-surface-default mx-auto flex max-w-xl flex-col items-center gap-3 rounded-lg border p-8 text-center">
              <CircleHelp
                className="text-iconMinimal h-8 w-8"
                strokeWidth={1.5}
                aria-hidden
              />
              <h1 className="text-heading-xs text-textDefault">
                Tracker details are unavailable
              </h1>
              <p className="text-body-sm text-textSubtle">
                Check the link and try again.
              </p>
            </section>
          )}
          {!isLoading && !isError && details && (
            <TrackerDetails details={details} signInHref={signInHref} />
          )}
        </main>
      </div>
    </PageWithTitle>
  );
}

export default PublicTrackerDetailPage;
